import React, { useState, useContext, useRef } from 'react';
import { LanguageContext } from '../../context/LanguageContext';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import { FaTimesCircle } from 'react-icons/fa';
import ReceiptPrinter from './payment/ReceiptPrinter';
import './ReturnOrderModal.css';

// Import new components
import SearchOrderForm from './return-order/SearchOrderForm';
import OrderDetails from './return-order/OrderDetails';
import ConfirmationDialog from './return-order/ConfirmationDialog';
import { prepareOrderForPrinting, loadPrinterSettings, getDefaultSettings } from './return-order/PrinterService';

const ReturnOrderModal = ({ isOpen, onClose }) => {
    const receiptPrinterRef = useRef();
    const { language } = useContext(LanguageContext);
    const [orderId, setOrderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleSearch = async () => {
        if (!orderId.trim()) return;

        const rawIdentifier = orderId.trim();
        const looksLikeCardIdentifier = (/^9\d{6,}$/.test(rawIdentifier) || /^ORD9/.test(rawIdentifier));
        
        setLoading(true);
        setError(null);
        try {
            // Pass a query parameter to indicate we want to search in both tables
            const response = await axios.get(`orders/${orderId}`, {
                params: {
                    search_all: true, // This parameter can tell the backend to search in both tables
                    // Prefer card for card identifiers to avoid numeric collisions with cash orders
                    ...(looksLikeCardIdentifier ? { order_type: 'card' } : {})
                }
            });
            
            if (!response.data || !response.data.data) {
                throw new Error('No data received');
            }

            const orderData = response.data.data;

            if (orderData.status === 'returned') {
                setError(language === 'ar' ? 'تم إرجاع هذا الطلب مسبقاً' : 'This order has already been returned');
                setOrder(null);
            } else {
                
                // Determine payment method based on order data or id format
                let paymentMethod = orderData.payment_method;
                
                // If payment_method isn't explicitly set, try to determine it
                if (!paymentMethod) {
                    // Check if this ID follows a pattern for card orders
                    const orderIdString = String(orderData.id || orderData.order_number || '');
                    if (
                        orderIdString.includes('CARD') ||
                        orderIdString.includes('card') ||
                        /^9\d{6,}$/.test(String(rawIdentifier)) ||
                        /^ORD9/.test(String(rawIdentifier))
                    ) {
                        paymentMethod = 'card';
                    } else {
                        // Default to cash if we can't determine
                        paymentMethod = 'cash';
                    }
                }
                
                // Determine if this is a card order based on the data source or API response
                const isCardOrder = paymentMethod === 'card' || 
                                  (orderData.order_number && orderData.order_number.includes('CARD')) ||
                                  orderData.resource_type === 'SalesOrderCardResource';
                
                const processedOrder = {
                    id: orderData.id,
                    order_number: orderData.order_number,
                    payment_method: paymentMethod,
                    customer_type: orderData.customer_type || 'customer',
                    company_name: orderData.company_name || '',
                    company_tax_number: orderData.company_tax_number || '',
                    items: orderData.items.map(item => {
                        
                        // Preserve the original item data exactly as received
                        return {
                            name: language === 'ar' ? item.name_ar : item.name,
                            name_ar: item.name_ar || '',
                            quantity: parseFloat(item.quantity) || 1,
                            unit_price: parseFloat(item.unit_price) || 0,
                            price: parseFloat(item.price || item.unit_price * item.quantity) || 0
                        };
                    }),
                    total: orderData.total,
                    created_at: orderData.created_at,
                    subtotal: orderData.subtotal || 0,
                    tax: orderData.tax || 0,
                    delivery_fee: orderData.delivery_fee || 0,
                    qr_code: orderData.qr_code,
                    // Add flag to identify card orders for processing
                    isCardOrder: isCardOrder
                };
                setOrder(processedOrder);
            }
        } catch (err) {
            console.error('Search Error:', err);
            setError(language === 'ar' ? 'لم يتم العثور على الطلب' : 'Order not found');
            setOrder(null);
        } finally {
            setLoading(false);
        }
    };

    const handleReturn = async () => {
        if (!order) return;

        setLoading(true);
        try {
            // Extract numeric ID from order_number or use the original ID
            const orderId = order.id.toString().startsWith('ORD') 
                ? order.id.replace('ORD', '').replace(/^0+/, '')
                : order.id;

            // Include a type parameter to indicate the order type to the backend
            const isCardOrder = order.payment_method === 'card' || 
                               String(order.id || order.order_number || '').includes('CARD') || 
                               String(order.id || order.order_number || '').includes('card') ||
                               /^9\d{6,}$/.test(String(orderId || '')) ||
                               /^ORD9/.test(String(orderId || ''));
            
            await axios.post(`orders/${orderId}/return`, {
                order_type: isCardOrder ? 'card' : 'cash', // This parameter tells the backend which table to update
                status: 'returned',
                return_date: new Date().toISOString()
            });
            toast.success(
                language === 'ar' 
                    ? 'تم إرجاع الطلب بنجاح' 
                    : 'Order returned successfully'
            );
            
            // Print the return receipt after successful return
            try {
                await handlePrintReceipt(true); // true = print as return receipt
            } catch (printError) {
                console.error('Error printing return receipt:', printError);
                // Don't block the return process if printing fails
            }
            
            setShowConfirmation(false);
            onClose();
        } catch (err) {
            console.error('Return Error:', err);
            
            // Check if this is a validation error about the order already being returned
            if (err.response && err.response.data) {
                // Check for specific error message from backend
                if (err.response.data.message === 'Order has already been returned') {
                    toast.error(
                        language === 'ar'
                            ? 'تم إرجاع هذا الطلب مسبقاً'
                            : 'This order has already been returned'
                    );
                    // Close the modal after showing the error
                    setShowConfirmation(false);
                    onClose();
                    return;
                }
                
                // Display any other error message from the backend
                if (err.response.data.message) {
                    toast.error(err.response.data.message);
                    return;
                }
            }
            
            // Generic error message as fallback
            toast.error(
                language === 'ar'
                    ? 'حدث خطأ أثناء إرجاع الطلب'
                    : 'Error returning the order'
            );
        } finally {
            setLoading(false);
        }
    };
    
    const handlePrintReceipt = async (isReturnReceipt = false) => {
        if (!order) return;

        try {
            // Check if the printer reference is available
            if (!receiptPrinterRef || !receiptPrinterRef.current) {
                console.error('Receipt printer reference is not available');
                toast.error(language === 'ar' ? 'مرجع الطابعة غير متوفر' : 'Printer reference not available');
                return;
            }
            
            // Attempt to connect to QZ-Tray first
            if (receiptPrinterRef.current.connectQZ) {
                try {
                    await receiptPrinterRef.current.connectQZ();
                } catch (connectError) {
                    console.error('Failed to connect to QZ-Tray:', connectError);
                    toast.error(language === 'ar' 
                        ? 'فشل الاتصال بـ QZ-Tray. هل هو قيد التشغيل؟' 
                        : 'Failed to connect to QZ-Tray. Is it running?');
                }
            }
            
            // Prepare order data for printing
            // First mark the order as a return order and preserve card order flag if present
            const orderWithReturnFlag = {
                ...order,
                isReturn: true,
                // Explicitly carry forward the isCardOrder flag
                isCardOrder: order.isCardOrder || false
            };
            
            const enhancedOrder = prepareOrderForPrinting(orderWithReturnFlag);
            // Explicitly set isReturn flag after preparation
            enhancedOrder.isReturn = true;
            
            // Ensure card order flag is preserved
            if (orderWithReturnFlag.isCardOrder) {
                enhancedOrder.isCardOrder = true;
            }
            
            // Get printer settings
            // eslint-disable-next-line no-unused-vars
            const settings = await loadPrinterSettings(language);
            
            // Set the receipt type based on the parameter
            enhancedOrder.isReturn = isReturnReceipt;
            
            // Print the receipt
            if (receiptPrinterRef.current) {
                try {
                    // Log QR code preview for debugging
                    if (enhancedOrder.qr_code) {
                        console.log('QR Code preview:', enhancedOrder.qr_code.substring(0, 50) + '...');
                    }
                    
                    await receiptPrinterRef.current.printReceipt(enhancedOrder);
                    toast.success(language === 'ar' ? 'جاري طباعة الإيصال' : 'Printing receipt');
                } catch (error) {
                    console.error('Error calling printReceipt:', error);
                    toast.error(language === 'ar' ? 'حدث خطأ أثناء طباعة الإيصال' : 'Error printing receipt');
                }
            }
            
            // Don't close the modal so they can print multiple times if needed
        } catch (err) {
            console.error('Print Error:', err);
            toast.error(
                language === 'ar'
                    ? 'حدث خطأ أثناء طباعة الإيصال'
                    : 'Error printing receipt'
            );
        }
    };

    return (
        <div className={`modal ${isOpen ? 'show' : ''}`} onClick={onClose}>
            {/* Hidden ReceiptPrinter component with ref */}
            <div style={{ display: 'none' }}>
                <ReceiptPrinter 
                    ref={receiptPrinterRef} 
                    settings={getDefaultSettings()} 
                />
            </div>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-medium">
                            {language === 'ar' ? 'البحث عن طلب' : 'Search Order'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <FaTimesCircle className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Search Form Component */}
                        <SearchOrderForm 
                            orderId={orderId}
                            setOrderId={setOrderId}
                            handleSearch={handleSearch}
                            loading={loading}
                            language={language}
                        />

                        {/* Error Message */}
                        {error && (
                            <p className="text-sm text-red-600">{error}</p>
                        )}

                        {/* Order Details Component */}
                        {order && (
                            <OrderDetails 
                                order={order}
                                language={language}
                                loading={loading}
                                handlePrintReceipt={handlePrintReceipt}
                                setShowConfirmation={setShowConfirmation}
                            />
                        )}
                    </div>

                    {/* Confirmation Dialog Component */}
                    <ConfirmationDialog 
                        showConfirmation={showConfirmation}
                        setShowConfirmation={setShowConfirmation}
                        handleReturn={handleReturn}
                        loading={loading}
                        language={language}
                    />
                </div>
            </div>
        </div>
    );
};

export default ReturnOrderModal;